import { MRT_ColumnDef } from "material-react-table";
import { TFunction } from "i18next";
import i18n from "i18n/config";
import dayjs from "shared/dayjs";
import SmartTruncatedText from "components/smart-truncated-text";
import { formatAmount, getUserTimeZone, roundToTwoDecimals } from "shared/utils/common";
import { TranslationList } from "shared/interfaces/core-interfaces";
import { TypedRequestRow } from "./state/request-interfaces";
import CellWithResponsesList from "./components/request-responses";
import { approvalListExport } from 'services/exportColumnHelpers';
import { numericColumnAlignment } from 'features/Finance/FinanceDocuments/Components/TableComponents/commonComponents';

export interface RequestTypeConfig {
    slug: string;
    label: string;
    // Matches the backend RequestTypeEnum. For finance documents any finance id returns the full set.
    requestTypeId: number;
    columns: (t: TFunction, dateFormat: string | undefined) => MRT_ColumnDef<TypedRequestRow>[];
    // Whether rows can carry attachments (expandable detail panel). Only finance
    // documents and absences return files today; other types have no panel content.
    hasDetailPanel?: boolean;
}

// ---- column factories -----------------------------------------------------

const dateColumn = (accessorKey: keyof TypedRequestRow, header: string, t: TFunction, dateFormat: string | undefined): MRT_ColumnDef<TypedRequestRow> => ({
    accessorKey,
    id: accessorKey as string,
    header: t(header)!,
    sortDescFirst: false,
    accessorFn: (d: any) => d[accessorKey] ? new Date(d[accessorKey]) : null,
    sortingFn: 'datetime',
    enableRanking: false,
    filterFn: (row, id, filterValue: string) => {
        const date = row.getValue<Date | null>(id);
        if (!date) return false;
        return dayjs(date).tz(getUserTimeZone()).format(dateFormat).toLowerCase().includes(filterValue.toLowerCase());
    },
    Cell: ({ cell }) => {
        const val = cell.getValue<Date | null>();
        return <SmartTruncatedText text={val ? dayjs(val).tz(getUserTimeZone()).format(dateFormat) : " - "} width="max" />;
    },
});

const textColumn = (accessorKey: keyof TypedRequestRow, header: string, t: TFunction, filterVariant?: 'autocomplete'): MRT_ColumnDef<TypedRequestRow> => ({
    accessorKey,
    header: t(header)!,
    ...(filterVariant ? { filterVariant } : {}),
    Cell: ({ cell }) => <SmartTruncatedText text={cell.getValue<string>() ?? " - "} width="max" />,
});

const moneyColumn = (accessorKey: keyof TypedRequestRow, header: string, t: TFunction): MRT_ColumnDef<TypedRequestRow> => ({
    accessorKey,
    header: t(header)!,
    ...numericColumnAlignment,
    Cell: ({ cell, row }) => {
        const val = cell.getValue<number | null>();
        return <SmartTruncatedText text={val != null ? `${formatAmount(val)} ${row.original.currency ?? ''}`.trim() : " - "} width="max" />;
    },
});

const numberColumn = (accessorKey: keyof TypedRequestRow, header: string, t: TFunction): MRT_ColumnDef<TypedRequestRow> => ({
    accessorKey,
    header: t(header)!,
    Cell: ({ cell }) => {
        const val = cell.getValue<number | null>();
        return <SmartTruncatedText text={val != null ? `${val}` : " - "} width="max" />;
    },
});

// Merges requested days + hours into a single readable, two-decimal-rounded "Duration"
// value (e.g. "1 day", "5 days and 8 hours"). Mirrors the Absences page column.
const durationColumn = (t: TFunction): MRT_ColumnDef<TypedRequestRow> => ({
    id: 'duration',
    header: t('DURATION')!,
    accessorFn: (d) => {
        const roundedDays = roundToTwoDecimals(d.requestedDays ?? 0);
        const roundedHours = roundToTwoDecimals(d.requestedHours ?? 0);
        const days = roundedDays ? `${roundedDays} ${roundedDays > 1 ? t('days') : t('day')}` : null;
        const hours = roundedHours ? `${roundedHours} ${roundedHours > 1 ? t('hours') : t('hour')}` : null;
        if (days && hours) return `${days} ${t('and')} ${hours}`;
        if (days) return days;
        if (hours) return hours;
        return " - ";
    },
    Cell: ({ cell }) => <SmartTruncatedText text={cell.getValue<string>()} width="max" />,
});

// Resolves a translated reference-data value (payment method, etc.): picks the current
// UI language, falling back to the record's default language. Mirrors the invoice list.
const paymentMethodColumn = (t: TFunction): MRT_ColumnDef<TypedRequestRow> => ({
    id: 'paymentMethod',
    header: t('PAYMENT METHOD')!,
    filterVariant: 'autocomplete',
    accessorFn: (d) => {
        const paymentMethod = d.paymentMethod as TranslationList | null | undefined;
        if (!paymentMethod) return '';
        const lang = i18n.language;
        const translationForLang = paymentMethod.names.find(n => n.language === lang)?.translation;
        if (translationForLang) return translationForLang;
        const defaultTranslation = paymentMethod.names.find(n => n.language === paymentMethod.defaultLanguage)?.translation;
        return defaultTranslation ?? '';
    },
    Cell: ({ cell }) => <SmartTruncatedText text={String(cell.getValue<string>() ?? '')} width="max" />,
});

// ---- shared columns -------------------------------------------------------

// `status` is translated when rows are mapped for the table (see RequestTypeView).
const statusColumn = (t: TFunction): MRT_ColumnDef<TypedRequestRow> => ({
    accessorKey: 'status', header: t('STATUS')!, filterVariant: 'autocomplete',
    Cell: ({ cell, row }) => <CellWithResponsesList requestResponses={row.original.requestResponses} value={cell.getValue<string>()} />,
});

const approvalListColumn = (t: TFunction): MRT_ColumnDef<TypedRequestRow> => ({
    accessorKey: 'approvalList', meta: approvalListExport, header: t('APPROVAL LIST')!,
    Cell: ({ row }) => <SmartTruncatedText text={row.original.approvalList} width="max" />,
});

// Whoever determined the terminal outcome (approvers on Approved, the decliner on Declined);
// empty for Pending/Canceled. Joined into one readable string so it filters and sorts as text.
// Header is "Resolved by" to pair with the "Resolved at" date column.
const respondedByColumn = (t: TFunction): MRT_ColumnDef<TypedRequestRow> => ({
    accessorKey: 'respondedBy', header: t('RESOLVED BY')!, filterVariant: 'autocomplete',
    accessorFn: (d) => d.respondedBy?.length ? d.respondedBy.join(', ') : ' - ',
    Cell: ({ cell }) => <SmartTruncatedText text={cell.getValue<string>()} width="max" />,
});

// `requestType` and `status` are translated when rows are mapped for the table (see RequestTypeView).
// `showType` adds the Type column — only useful for types with several distinct names
// (finance documents, absences). For single-type lists it just repeats the same value.
const withSharedColumns = (t: TFunction, dateFormat: string | undefined, typeSpecific: MRT_ColumnDef<TypedRequestRow>[], showType = false): MRT_ColumnDef<TypedRequestRow>[] => [
    dateColumn('createdAt', 'CREATED AT', t, dateFormat),
    { accessorKey: 'requestedBy', filterVariant: 'autocomplete', header: t('CREATED BY')! },
    ...(showType ? [{ accessorKey: 'requestType', filterVariant: 'autocomplete', header: t('TYPE')! } as MRT_ColumnDef<TypedRequestRow>] : []),
    statusColumn(t),
    { accessorKey: 'tenant', filterVariant: 'autocomplete', header: t('TENANT')! },
    // Order: Approval list (pending only) → Resolved at → Resolved by. The per-tab column
    // filtering in RequestTypeView drops Approval list on All/My and the resolved pair on Pending.
    approvalListColumn(t),
    dateColumn('resolvedAt', 'RESOLVED AT', t, dateFormat),
    respondedByColumn(t),
    ...typeSpecific,
];

// ---- registry -------------------------------------------------------------

export const requestTypeConfigs: RequestTypeConfig[] = [
    {
        slug: 'finance-documents',
        label: 'Financial documents',
        requestTypeId: 1, // any finance id returns all finance document types
        hasDetailPanel: true,
        columns: (t, df) => withSharedColumns(t, df, [
            paymentMethodColumn(t),
            textColumn('clientSupplier', 'CLIENT/SUPPLIER', t, 'autocomplete'),
            textColumn('documentNumber', 'DOCUMENT NUMBER', t),
            moneyColumn('net', 'NET', t),
            moneyColumn('gross', 'GROSS', t),
            textColumn('currency', 'CURRENCY', t, 'autocomplete'),
            dateColumn('documentDate', 'DOCUMENT DATE', t, df),
            dateColumn('paymentDeadline', 'PAYMENT DEADLINE', t, df),
            textColumn('costCenter', 'PROFIT/COST CENTER', t, 'autocomplete'),
        ], true),
    },
    {
        slug: 'absences',
        label: 'Absences',
        requestTypeId: 18,
        hasDetailPanel: true,
        // Custom column order (not withSharedColumns): the absence-specific columns are
        // interleaved with the shared ones — Duration/From/To sit right after Tenant, then the
        // approval/resolution columns, and Description last. Per-tab filtering (RequestTypeView)
        // drops Approval list on All/My and the Resolved at/by pair on Pending.
        columns: (t, df) => [
            dateColumn('createdAt', 'CREATED AT', t, df),
            { accessorKey: 'requestedBy', filterVariant: 'autocomplete', header: t('CREATED BY')! },
            { accessorKey: 'requestType', filterVariant: 'autocomplete', header: t('TYPE')! },
            statusColumn(t),
            { accessorKey: 'tenant', filterVariant: 'autocomplete', header: t('TENANT')! },
            durationColumn(t),
            dateColumn('from', 'FROM', t, df),
            dateColumn('to', 'TO', t, df),
            approvalListColumn(t),
            dateColumn('resolvedAt', 'RESOLVED AT', t, df),
            respondedByColumn(t),
            textColumn('description', 'DESCRIPTION', t),
        ],
    },
    {
        slug: 'hiring',
        label: 'Hiring',
        requestTypeId: 15,
        columns: (t, df) => withSharedColumns(t, df, []),
    },
    {
        slug: 'refunds',
        label: 'Refunds',
        requestTypeId: 21,
        columns: (t, df) => withSharedColumns(t, df, [
            textColumn('supplier', 'SUPPLIER', t),
            moneyColumn('sum', 'SUM', t),
            moneyColumn('sumWithoutTax', 'SUM WITHOUT TAX', t),
            textColumn('currency', 'CURRENCY', t, 'autocomplete'),
            textColumn('description', 'DESCRIPTION', t),
        ]),
    },
    {
        slug: 'travel-plan',
        label: 'Travel plans',
        requestTypeId: 19,
        columns: (t, df) => withSharedColumns(t, df, [
            textColumn('traveler', 'TRAVELER', t),
            textColumn('from', 'FROM', t),
            textColumn('to', 'TO_Place', t),
            dateColumn('startDate', 'START DATE', t, df),
            dateColumn('endDate', 'END DATE', t, df),
            textColumn('reasonForTravel', 'REASON FOR TRAVEL', t),
        ]),
    },
    {
        // Second approval on a travel plan: the allowances card (post-trip settlement) is approved
        // separately from the trip. Same row shape as the trip - both requests carry the travel
        // plan as their entity - so the columns are identical.
        slug: 'travel-plan-allowance',
        label: 'Travel plan allowances',
        requestTypeId: 24,
        columns: (t, df) => withSharedColumns(t, df, [
            textColumn('traveler', 'TRAVELER', t),
            textColumn('from', 'FROM', t),
            textColumn('to', 'TO_Place', t),
            dateColumn('startDate', 'START DATE', t, df),
            dateColumn('endDate', 'END DATE', t, df),
            textColumn('reasonForTravel', 'REASON FOR TRAVEL', t),
        ]),
    },
    {
        slug: 'bonus-commission',
        label: 'Bonuses & commissions',
        requestTypeId: 22,
        columns: (t, df) => withSharedColumns(t, df, [
            textColumn('employee', 'EMPLOYEE', t, 'autocomplete'),
            dateColumn('from', 'FROM', t, df),
            dateColumn('to', 'TO', t, df),
            moneyColumn('amount', 'AMOUNT', t),
            textColumn('currency', 'CURRENCY', t, 'autocomplete'),
            textColumn('paymentInterval', 'PAYMENT INTERVAL', t),
            textColumn('comment', 'COMMENT', t),
        ]),
    },
    {
        slug: 'salary-deduction',
        label: 'Salary deductions',
        requestTypeId: 23,
        columns: (t, df) => withSharedColumns(t, df, [
            textColumn('employee', 'EMPLOYEE', t, 'autocomplete'),
            dateColumn('from', 'FROM', t, df),
            dateColumn('to', 'TO', t, df),
            moneyColumn('amount', 'AMOUNT', t),
            textColumn('currency', 'CURRENCY', t, 'autocomplete'),
            textColumn('paymentInterval', 'PAYMENT INTERVAL', t),
            textColumn('comment', 'COMMENT', t),
        ]),
    },
    {
        slug: 'month-reopen',
        label: 'Month reopen',
        requestTypeId: 20,
        columns: (t, df) => withSharedColumns(t, df, [
            numberColumn('year', 'YEAR', t),
            numberColumn('month', 'MONTH', t),
            textColumn('description', 'DESCRIPTION', t),
        ]),
    },
];

export const getRequestTypeBySlug = (slug: string | undefined): RequestTypeConfig | undefined =>
    requestTypeConfigs.find(c => c.slug === slug);

// Each non-finance module owns a single request type id; every other id
// (all finance document types: 1-14, 16, 17) rolls up to finance-documents.
const specificSlugByTypeId = new Map<number, string>(
    requestTypeConfigs.filter(c => c.slug !== 'finance-documents').map(c => [c.requestTypeId, c.slug]));

export const getSlugForRequestTypeId = (typeId: number): string =>
    specificSlugByTypeId.get(typeId) ?? 'finance-documents';
